import { useState, useEffect } from "react";
import { SvgNames } from "@sunrun/experience-ui-components";
import type { Path } from "router";
import { SalesExpApi } from "providers/Api";
import { useHybridToken } from "providers/Auth";

type NavigationItemsResponse = {
  navigationItems: NavigationItemApi[];
  mfeConfig?: never; // Do not access this field from this repository. Instead you should import this information from @sunrun/sales-experience-shared.
};

type NavigationItemApi = {
  id: string;
  href: string;
  svgName: string;
  label: string;
};

type NavigationItem = {
  id: string;
  path: Path;
  svgName: string;
  label: string;
};

const navigationItemApiToNavigationItem = (
  item: NavigationItemApi
): NavigationItem => {
  const path = new URL(item.href).pathname as Path;
  return {
    id: item.id,
    path,
    svgName: item.svgName,
    label: item.label,
  };
};

const useNavigationItems = () => {
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const hybridToken = useHybridToken();

  // Fetch navigation items when token changes
  useEffect(() => {
    const fetchNavigationItems = async () => {
      setLoading(true);

      try {
        if (!hybridToken) {
          // Return default navigation items when no token is available
          setNavigationItems([
            {
              id: "support",
              path: "/support",
              svgName: SvgNames.Help,
              label: "Support",
            },
            {
              id: "profile",
              path: "/profile",
              svgName: SvgNames.Account,
              label: "Profile",
            },
          ]);
          setLoading(false);
          return;
        }

        // Use the SalesExpApi class to call our navigation endpoint
        const response = await SalesExpApi.getNavigationItems(hybridToken);
        setNavigationItems(
          response.navigationItems.map(navigationItemApiToNavigationItem)
        );
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Unknown error"));

        // Fallback to basic navigation items in case of error
        setNavigationItems([
          {
            id: "support",
            path: "/support",
            svgName: SvgNames.Help,
            label: "Support",
          },
          {
            id: "profile",
            path: "/profile",
            svgName: SvgNames.Account,
            label: "Profile",
          },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchNavigationItems();
  }, [hybridToken]); // Refresh when hybridToken changes

  return { navigationItems, loading, error };
};

export type { NavigationItemApi, NavigationItem, NavigationItemsResponse };
export { useNavigationItems };
